import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";

export default function BookingSuccessPage() {
  const { t } = useLang();
  const { search } = useLocation();
  const sessionId = new URLSearchParams(search).get("session_id");
  const [status, setStatus] = useState("checking"); // checking|paid|failed
  const [booking, setBooking] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/bookings/status/${sessionId}`);
        setBooking(data.booking);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.payment_status === "expired" || data.payment_status === "failed") { setStatus("failed"); return; }
        attempts.current += 1;
        if (attempts.current >= 8) { setStatus("failed"); return; }
        timer = setTimeout(poll, 2000);
      } catch { setStatus("failed"); }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [sessionId]);

  return (
    <main data-testid="success-page" className="min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--foreground))] pt-32 pb-24">
      <div className="container-x max-w-2xl text-center">
        {status === "checking" && (
          <>
            <div className="overline mb-4">···</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[hsl(var(--foreground))]">{t.booking.checking}</h1>
            <div className="mt-10 inline-block w-10 h-10 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin" />
          </>
        )}
        {status === "paid" && (
          <>
            <div className="overline mb-4 !text-[hsl(var(--accent))]">✓</div>
            <h1 data-testid="success-title" className="font-serif text-4xl md:text-6xl text-[hsl(var(--foreground))]">{t.booking.successTitle}</h1>
            <p className="mt-6 text-[hsl(var(--foreground))]/85 max-w-lg mx-auto">{t.booking.successSub}</p>
            {booking && (
              <div className="mt-12 inline-block text-left bg-[hsl(var(--card))] border border-[hsl(var(--foreground))]/10 p-8">
                <div className="overline mb-4">Booking</div>
                <div className="space-y-2 text-sm text-[hsl(var(--foreground))]/80">
                  <div><span className="text-[hsl(var(--foreground))]/88">ID:</span> {booking.id}</div>
                  <div><span className="text-[hsl(var(--foreground))]/88">Check-in:</span> {booking.check_in_iso}</div>
                  <div><span className="text-[hsl(var(--foreground))]/88">Check-out:</span> {booking.check_out_iso}</div>
                  <div><span className="text-[hsl(var(--foreground))]/88">Total:</span> <span className="text-[hsl(var(--accent))] font-semibold">€{Number(booking.total_eur).toFixed(0)}</span></div>
                </div>
                <a
                  data-testid="download-ics"
                  href={`${api.defaults.baseURL}/bookings/${booking.id}/ics`}
                  download
                  className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--accent))] border-b border-[hsl(var(--accent))]/40 pb-1 hover:text-[hsl(var(--foreground))] hover:border-white transition-colors"
                >
                  Kalender (.ics) <span aria-hidden>↓</span>
                </a>
              </div>
            )}
            <div className="mt-12"><Link to="/" className="btn-primary">{t.booking.backHome}</Link></div>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="overline mb-4 !text-red-400">!</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[hsl(var(--foreground))]">{t.booking.failedTitle}</h1>
            <p className="mt-6 text-[hsl(var(--foreground))]/85">{t.booking.failedSub}</p>
            <div className="mt-10"><Link to="/book" className="btn-primary">{t.nav.book}</Link></div>
          </>
        )}
      </div>
    </main>
  );
}
